import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getMember,
  getSensors,
  createSensor,
  deleteSensor,
  approveUser,
  type Member,
  type Sensor
} from '../lib/admin';
import { getUser } from '../lib/auth';
import { Users, Plus, Trash2, ArrowLeft, Mail, CheckCircle, XCircle, Clock, Key } from 'lucide-react';
import { useToast, useConfirm } from './ui';

export default function SensorManagementForMember() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const currentUser = getUser();
  const toast = useToast();
  const { confirm } = useConfirm();

  const [member, setMember] = useState<Member | null>(null);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    full_name: ''
  });

  useEffect(() => {
    if (memberId) {
      loadData();
    }
  }, [memberId]);

  const loadData = async () => {
    if (!memberId) return;

    try {
      setLoading(true);
      setError('');

      const [memberData, sensorsData] = await Promise.all([
        getMember(memberId),
        getSensors({ member_id: memberId })
      ]);

      setMember(memberData);
      setSensors(sensorsData);

    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.full_name || !member) {
      toast.showWarning('E-post og fullt navn er påkrevd');
      return;
    }

    try {
      setSaving(true);
      const result = await createSensor({
        email: formData.email,
        full_name: formData.full_name,
        member_id: member.id,
        study_association_id: member.study_association_id
      });

      // Show temp password to admin
      setTempPassword(result.temp_password);

      toast.showSuccess(`Sensor opprettet! Midlertidig passord: ${result.temp_password} - Kopier passordet nå, det vises ikke igjen!`);

      setShowCreateForm(false);
      resetForm();
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved opprettelse: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sensor: Sensor) => {
    const confirmed = await confirm({
      title: 'Deaktiver sensor',
      message: `Er du sikker på at du vil deaktivere ${sensor.full_name}? Alle sensorens graderingsdata vil beholdes, men tilgangen til systemet fjernes.`,
      confirmLabel: 'Deaktiver',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteSensor(sensor.id);
      toast.showSuccess(`${sensor.full_name} er deaktivert`);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved deaktivering: ${err.message}`);
    }
  };

  const handleApprove = async (sensorId: string) => {
    const confirmed = await confirm({
      title: 'Godkjenn sensor',
      message: 'Er du sikker på at du vil godkjenne denne sensoren?',
      confirmLabel: 'Godkjenn',
      cancelLabel: 'Avbryt',
      variant: 'default'
    });

    if (!confirmed) {
      return;
    }

    try {
      await approveUser(currentUser.id, sensorId);
      toast.showSuccess('Sensor godkjent! Sensoren kan nå logge inn.');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved godkjenning: ${err.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: ''
    });
  };

  const handleBack = () => {
    navigate('/members');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B5651D] mx-auto mb-4"></div>
          <p className="text-gray-600">Laster sensorer...</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Medlem ikke funnet
        </div>
        <button
          onClick={handleBack}
          className="mt-4 flex items-center text-[#B5651D] hover:text-[#8B4513]"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Tilbake til medlemmer
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={handleBack}
          className="flex items-center text-[#B5651D] hover:text-[#8B4513] mb-4 transition"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Tilbake til medlemmer
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Sensorer for {member.organization_name}
            </h1>
            <p className="text-gray-600">
              Administrer sensorer tilknyttet denne medlemsorganisasjonen
            </p>
          </div>
          {(currentUser.role === 'systemadmin' || currentUser.role === 'admin' || currentUser.role === 'member') && (
            <button
              onClick={() => {
                setShowCreateForm(true);
                setTempPassword(null);
                resetForm();
              }}
              className="flex items-center space-x-2 bg-[#B5651D] text-white px-4 py-2 rounded-lg hover:bg-[#8B4513] transition"
            >
              <Plus className="w-5 h-5" />
              <span>Inviter Sensor</span>
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Temp Password Display */}
      {tempPassword && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Key className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-yellow-900 mb-2">
                Midlertidig Passord (vises kun én gang!)
              </h3>
              <div className="bg-white rounded p-3 mb-2">
                <code className="text-lg font-mono text-gray-900">{tempPassword}</code>
              </div>
              <p className="text-sm text-yellow-800">
                Kopier dette passordet og del det med sensoren. Det vil ikke vises igjen.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Inviter Ny Sensor
          </h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fullt Navn *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-post *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1">Hva skjer etter invitasjon:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Et midlertidig passord genereres automatisk</li>
                <li>Sensor må godkjennes av admin før første pålogging</li>
                <li>Sensor mottar e-post med innloggingsdetaljer</li>
                <li>Sensor kan endre passord etter første pålogging</li>
              </ul>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
              >
                {saving ? 'Oppretter...' : 'Inviter Sensor'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sensors List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sensor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  E-post
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tildelte Kurs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opprettet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sensors.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>Ingen sensorer funnet for denne organisasjonen</p>
                    <p className="text-sm mt-1">Klikk "Inviter Sensor" for å legge til en ny sensor</p>
                  </td>
                </tr>
              ) : (
                sensors.map((sensor) => (
                  <tr key={sensor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {sensor.full_name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        {sensor.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        sensor.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : sensor.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {sensor.status === 'active' ? (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Aktiv
                          </>
                        ) : sensor.status === 'pending' ? (
                          <>
                            <Clock className="w-3 h-3 mr-1" />
                            Avventer Godkjenning
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" />
                            Inaktiv
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {sensor.assigned_courses_count || 0} kurs
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(sensor.created_at).toLocaleDateString('nb-NO')}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center space-x-2">
                        {sensor.status === 'pending' && (
                          <button
                            onClick={() => handleApprove(sensor.id)}
                            className="text-green-600 hover:text-green-800 transition"
                            title="Godkjenn sensor"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                        )}
                        {sensor.status === 'active' && (currentUser.role === 'systemadmin' || currentUser.role === 'admin' || currentUser.role === 'member') && (
                          <button
                            onClick={() => handleDelete(sensor)}
                            className="text-red-600 hover:text-red-800 transition"
                            title="Deaktiver sensor"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      {sensors.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt Sensorer</p>
                <p className="text-2xl font-bold text-gray-900">{sensors.length}</p>
              </div>
              <Users className="w-8 h-8 text-[#B5651D]" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aktive Sensorer</p>
                <p className="text-2xl font-bold text-green-600">
                  {sensors.filter(s => s.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avventer Godkjenning</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {sensors.filter(s => s.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
